import { Expense } from "../../lib/types";
import { expenseAPI } from "../api/api";

// Database operations - API client implementation
const dbOperations = {
  getAllExpenses: async (): Promise<Expense[]> => {
    try {
      return await expenseAPI.getAll();
    } catch (error) {
      console.error("Error getting all expenses:", error);
      return [];
    }
  },

  getExpenseById: async (id: string): Promise<Expense | null> => {
    try {
      return await expenseAPI.getById(id);
    } catch (error) {
      console.error(`Error getting expense with id ${id}:`, error);
      return null;
    }
  },

  addExpense: async (expense: Omit<Expense, "id">): Promise<Expense> => {
    try {
      return await expenseAPI.add(expense);
    } catch (error) {
      console.error("Error adding expense:", error);
      throw error;
    }
  },

  updateExpense: async (expense: Expense): Promise<Expense> => {
    try {
      return await expenseAPI.update(expense);
    } catch (error) {
      console.error("Error updating expense:", error);
      throw error;
    }
  },

  deleteExpense: async (id: string): Promise<void> => {
    try {
      await expenseAPI.delete(id);
    } catch (error) {
      console.error("Error deleting expense:", error);
      throw error;
    }
  },

  togglePaid: async (id: string): Promise<Expense> => {
    try {
      return await expenseAPI.togglePaid(id);
    } catch (error) {
      console.error("Error toggling paid status:", error);
      throw error;
    }
  },
};

// Backward compatibility for non-async code
// This creates synchronous versions of the async methods that return empty data
// This is just for transitioning to the new API
const syncCompatibility = {
  getAllExpenses: (): Expense[] => {
    console.warn(
      "Using deprecated synchronous getAllExpenses. Update to async version."
    );
    return [];
  },

  getExpenseById: (id: string): Expense | null => {
    console.warn(
      "Using deprecated synchronous getExpenseById. Update to async version."
    );
    return null;
  },

  addExpense: (expense: Omit<Expense, "id">): Expense => {
    console.warn(
      "Using deprecated synchronous addExpense. Update to async version."
    );
    expenseAPI.add(expense).catch((e) => console.error(e));
    return { ...expense, id: "temp-id" } as Expense;
  },

  updateExpense: (expense: Expense): void => {
    console.warn(
      "Using deprecated synchronous updateExpense. Update to async version."
    );
    expenseAPI.update(expense).catch((e) => console.error(e));
  },

  deleteExpense: (id: string): void => {
    console.warn(
      "Using deprecated synchronous deleteExpense. Update to async version."
    );
    expenseAPI.delete(id).catch((e) => console.error(e));
  },

  togglePaid: (id: string): void => {
    console.warn(
      "Using deprecated synchronous togglePaid. Update to async version."
    );
    expenseAPI.togglePaid(id).catch((e) => console.error(e));
  },
};

// Export both async API and sync compatibility layer
const database = {
  ...dbOperations,
  ...syncCompatibility,
};

export default database;
